import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, UserIcon, BellIcon } from './icons';
import { CATALOGUE_ENABLED } from '../config/features';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // The nav is always a solid bar (not transparent over the hero) so the
  // DentoPharma logo and branding stay clearly visible on every page.
  const isOverHero = false;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navItems = [
    { name: 'Home', path: '/' },
    ...(CATALOGUE_ENABLED ? [{ name: 'Catalogue', path: '/catalogue' }] : []),
    { name: 'Blog', path: '/blog' },
    { name: 'Activities & Events', path: '/events' },
    { name: 'Our Mission', path: '/mission' },
    { name: 'Get Inspired', path: '/inspired' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-gray-200 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20 sm:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className="w-16 h-16 sm:w-[76px] sm:h-[76px] lg:w-[88px] lg:h-[88px] transition-all duration-300 group-hover:scale-105">
              <img 
                src="/images/DentoPharma Logo - PNG.png" 
                alt="DentoPharma Logo" 
                className={`w-full h-full object-contain transition-all duration-300 ${
                  isOverHero ? 'filter drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]' : ''
                }`}
              />
            </div>
          </Link>

          {/* Centered Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-4 lg:space-x-6 absolute left-1/2 transform -translate-x-1/2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative font-medium transition-all duration-300 px-2 py-1 ${
                  isOverHero
                    ? 'text-white hover:text-[#8EC641] drop-shadow-lg'
                    : isScrolled 
                    ? 'text-gray-700 hover:text-[#8EC641]' 
                    : 'text-gray-700 hover:text-[#8EC641] drop-shadow-lg'
                } ${isActive(item.path) ? (isOverHero ? 'text-white' : 'text-[#0D56A6]') : ''}`}
              >
                <span className="relative z-10 text-xs lg:text-sm">{item.name}</span>
                {isOverHero && (
                  <motion.div
                    className="absolute inset-0 bg-white/10 rounded-lg -z-10"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileHover={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
                {isActive(item.path) && (
                  <motion.div
                    layoutId="activeTab"
                    className={`absolute -bottom-1 left-0 right-0 h-0.5 rounded-full ${
                      isOverHero ? 'bg-white' : 'bg-[#8EC641]'
                    }`}
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button
              className={`p-2.5 rounded-2xl transition-all duration-300 ${
                isOverHero
                  ? 'text-white hover:text-[#8EC641] hover:bg-white/10 drop-shadow-lg'
                  : isScrolled
                  ? 'text-gray-600 hover:text-[#8EC641] hover:bg-gray-100'
                  : 'text-gray-600 hover:text-[#8EC641] hover:bg-gray-100'
              }`}
            >
              <BellIcon size={20} />
            </button>

            {/* User/Login */}
            <Link
              to="/login"
              className={`p-2.5 rounded-2xl transition-all duration-300 ${
                isOverHero
                  ? 'text-white hover:text-[#8EC641] hover:bg-white/10 drop-shadow-lg'
                  : isScrolled
                  ? 'text-gray-600 hover:text-[#8EC641] hover:bg-gray-100'
                  : 'text-gray-600 hover:text-[#8EC641] hover:bg-gray-100'
              }`}
            >
              <UserIcon size={20} />
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`xl:hidden p-2.5 rounded-2xl transition-all duration-300 ${
                isOverHero
                  ? 'text-white hover:text-[#8EC641] hover:bg-white/10 drop-shadow-lg'
                  : isScrolled
                  ? 'text-gray-600 hover:text-[#8EC641] hover:bg-gray-100'
                  : 'text-gray-600 hover:text-[#8EC641] hover:bg-gray-100'
              }`}
            >
              <motion.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isOpen ? <X size={20} /> : <Menu size={20} />}
              </motion.div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`xl:hidden border-t ${
              isOverHero
                ? 'bg-black/20 backdrop-blur-lg border-white/10'
                : isScrolled 
                ? 'bg-white/95 border-gray-200' 
                : 'bg-white/95 border-gray-200'
            }`}
          >
            <div className="container-custom py-4">
              <div className="flex flex-col space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      isOverHero
                        ? isActive(item.path)
                          ? 'bg-white/20 text-white'
                          : 'text-white hover:bg-white/10'
                        : isScrolled
                        ? isActive(item.path)
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-700 hover:bg-gray-50'
                        : isActive(item.path)
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className={`pt-4 border-t ${isOverHero ? 'border-white/20' : 'border-gray-200'}`}>
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="block w-full text-center bg-gradient-to-r from-[#0D56A6] to-[#0C4F9A] text-white py-3 px-4 rounded-2xl font-medium hover:from-[#0C4F9A] hover:to-[#0B498E] transition-all duration-300 shadow-lg"
                  >
                    Sign In
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
